import { faHand, faMegaphone, faMessages } from '@fortawesome/pro-regular-svg-icons'
import {
  faHand as faHandSolid,
  faMegaphone as faMegaphoneSolid,
  faMessages as faMessagesSolid,
} from '@fortawesome/pro-solid-svg-icons'

import { MODERATOR_LEVEL, TUTOR_LEVEL, type WorldLevel } from '@/queries/constants'

import type { IconStateDefinitions } from '@/components/UI/Icon/types'

import type {
  ChannelType,
  PostType,
  ProgramSubworldSlug,
  ProgramUmbrella,
  QuestionType,
  WorldVisibility,
} from './types'

export const MATH_DOMAIN = 'Math'
export const SCIENCE_DOMAIN = 'Science'
export const COLLEGE_READINESS_DOMAIN = 'College Readiness'
export const OTHER_DOMAIN = 'Other Subjects'
export const SCHOOL_PARTNERSHIPS_DOMAIN = 'School Partnerships'
export const WORLD_DOMAINS = [
  MATH_DOMAIN,
  SCIENCE_DOMAIN,
  COLLEGE_READINESS_DOMAIN,
  OTHER_DOMAIN,
  SCHOOL_PARTNERSHIPS_DOMAIN,
  'Community',
] as const

export const WORLD_HELP_INTENTS = ['receive-help', 'give-help'] as const

export const CHANNEL_TYPE_TO_ICON: Record<ChannelType, IconStateDefinitions> = {
  question: { inactive: faHand, active: faHandSolid },
  discussion: { inactive: faMessages, active: faMessagesSolid },
  announcement: { inactive: faMegaphone, active: faMegaphoneSolid },
}

export const CHANNEL_TYPE_TO_HEADING: Record<ChannelType, string> = {
  question: 'Question',
  discussion: 'Post',
  announcement: 'Announcement',
}

export const CHANNEL_TYPE_TO_POST_TYPE: Record<ChannelType, string> = {
  question: 'question',
  discussion: 'post',
  announcement: 'announcement',
}

export const QUESTION_TYPE_TO_HEADER_TEXT: Record<QuestionType, string> = {
  'problem-help': 'needs help with a problem',
  'assignment-help': 'needs help with an assignment',
  'topic-review': 'wants to review topics in a session',
}

export const POST_STATUSES = ['not-solved', 'closed', 'solved'] as const

export const POST_SCOPES = ['channel', 'world', 'global'] as const

export const POST_EVENT_TYPES = [
  'comment',
  'mark-solved',
  'mark-not-solved',
  'mark-closed',
  'request-live-help',
  'cancel-live-help-request',
  'open-live-help-session',
  'learner-left-live-help-session',
  'tutor-canceled-giving-live-help',
  'auto-cancel-live-help-request',
  'auto-close-post',
] as const

export const SCHOOLHOUSE_HUB_WORLD_SLUG = 'hub'
export const BETA_TESTING_WORLD_SLUG = 'testing'
export const SCHOOLHOUSE_TUTORS_WORLD_SLUG = 'tutors'
export const SAT_PREP_WORLD_SLUG = 'sat'
export const PHYSICS_WORLD_SLUG = 'physics'
export const BIOLOGY_WORLD_SLUG = 'biology'
export const CHEMISTRY_WORLD_SLUG = 'chemistry'
export const COMPUTER_SCIENCE_WORLD_SLUG = 'computer-science'
export const COLLEGE_PREP_WORLD_SLUG = 'college-prep'
export const PRE_CALCULUS_WORLD_SLUG = 'pre-calculus'
export const SAT_BOOTCAMP_WORLD_SLUG = 'sat-bootcamp'
export const CALCULUS_WORLD_SLUG = 'calculus'
export const PRE_ALGEBRA_WORLD_SLUG = 'pre-algebra'
export const ALGEBRA_WORLD_SLUG = 'algebra'
export const ALGEBRA_2_WORLD_SLUG = 'algebra2'
export const GEOMETRY_WORLD_SLUG = 'geometry'
export const STATISTICS_WORLD_SLUG = 'statistics'
export const ENRICHMENT_WORLD_SLUG = 'enrichment'
export const MODERATORS_WORLD_SLUG = 'moderators'
export const PRODUCT_WORLD_SLUG = 'shw-product'
export const KA_TEACHERS_WORLD_SLUG = 'ka-teachers'
export const KEY_CLUB_WORLD_SLUG = 'key-club'
export const COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG = 'college-admissions-workshops'
export const AI_SPECIAL_OPS_WORLD_SLUG = 'ai-ops'
export const JACKSON_WORLD_SLUG = 'jackson'
export const TORONTO_GROUP_WORLD_SLUG = 'toronto-kwik-learning-lab'
export const TORONTO_1_ON_1_WORLD_SLUG = 'toronto-kwik-1-on-1'
export const READING_AND_WRITING_WORLD_SLUG = 'reading-and-writing'
export const HISTORY_WORLD_SLUG = 'history'
export const COLLEGE_LOUNGE_WORLD_SLUG = 'college-lounge'
export const ALGEBRA2_LEARNING_LABS_WORLD_SLUG = 'algebra2-learning-labs'
export const PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG = 'pre-calculus-learning-labs'
export const KA_LEARNING_LABS_PROGRAM_UMBRELLA_SLUG = 'ka-learning-labs'
export const DIALOGUES_WORLD_SLUG = 'dialogues'

export const SCHOOL_PARTNERSHIP_WORLD_SLUGS: string[] = [
  JACKSON_WORLD_SLUG,
  TORONTO_GROUP_WORLD_SLUG,
  TORONTO_1_ON_1_WORLD_SLUG,
]

export const COMMUNITY_WORLD_SLUGS: string[] = [
  BETA_TESTING_WORLD_SLUG,
  PRODUCT_WORLD_SLUG,
  SCHOOLHOUSE_HUB_WORLD_SLUG,
  SCHOOLHOUSE_TUTORS_WORLD_SLUG,
  MODERATORS_WORLD_SLUG,
  KA_TEACHERS_WORLD_SLUG,
  AI_SPECIAL_OPS_WORLD_SLUG,
  KEY_CLUB_WORLD_SLUG,
  COLLEGE_LOUNGE_WORLD_SLUG,
]

export const PROGRAM_SUBWORLD_SLUGS = [
  COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
  SAT_BOOTCAMP_WORLD_SLUG,
  ALGEBRA2_LEARNING_LABS_WORLD_SLUG,
  PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG,
  DIALOGUES_WORLD_SLUG,
] as const

export const PROGRAM_UMBRELLAS = [KA_LEARNING_LABS_PROGRAM_UMBRELLA_SLUG] as const

const MATH_WORLD_SLUGS: string[] = [
  PRE_ALGEBRA_WORLD_SLUG,
  ALGEBRA_WORLD_SLUG,
  ALGEBRA_2_WORLD_SLUG,
  GEOMETRY_WORLD_SLUG,
  PRE_CALCULUS_WORLD_SLUG,
  CALCULUS_WORLD_SLUG,
  STATISTICS_WORLD_SLUG,
]

const SCIENCE_WORLD_SLUGS: string[] = [BIOLOGY_WORLD_SLUG, CHEMISTRY_WORLD_SLUG, PHYSICS_WORLD_SLUG]

export const TEST_PREP_WORLD_SLUGS: string[] = [SAT_PREP_WORLD_SLUG, SAT_BOOTCAMP_WORLD_SLUG]

export const KA_LEARNING_LABS_WORLD_SLUGS = [
  ALGEBRA2_LEARNING_LABS_WORLD_SLUG,
  PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG,
] as const

export const PROGRAM_UMBRELLA_TO_PROGRAM_WORLDS: Record<ProgramUmbrella, ProgramSubworldSlug[]> = {
  [KA_LEARNING_LABS_PROGRAM_UMBRELLA_SLUG]: [...KA_LEARNING_LABS_WORLD_SLUGS],
}

export const PROGRAM_WORLDS_TO_PROGRAM_UMBRELLAS: Record<
  ProgramSubworldSlug,
  ProgramUmbrella | null
> = {
  [ALGEBRA2_LEARNING_LABS_WORLD_SLUG]: KA_LEARNING_LABS_PROGRAM_UMBRELLA_SLUG,
  [PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG]: KA_LEARNING_LABS_PROGRAM_UMBRELLA_SLUG,
  [COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG]: null,
  [SAT_BOOTCAMP_WORLD_SLUG]: null,
  [DIALOGUES_WORLD_SLUG]: null,
}

export const CERTIFICATION_WORLD_SLUGS: string[] = [
  ...MATH_WORLD_SLUGS,
  ...SCIENCE_WORLD_SLUGS,
  ...TEST_PREP_WORLD_SLUGS,
  ...KA_LEARNING_LABS_WORLD_SLUGS,
  HISTORY_WORLD_SLUG,
  COMPUTER_SCIENCE_WORLD_SLUG,
  READING_AND_WRITING_WORLD_SLUG,
]

export const WORLDS_BY_LEVEL_ACCESS: Partial<Record<WorldLevel, string>> = {
  [TUTOR_LEVEL]: SCHOOLHOUSE_TUTORS_WORLD_SLUG,
  [MODERATOR_LEVEL]: MODERATORS_WORLD_SLUG,
}

export const MEMBER_POSTS_PER_WEEK_LIMIT = 5

export const AUTO_CANCEL_LIVE_HELP_AFTER_OFFLINE_FOR_MINUTES = 15
export const AUTO_CLOSE_POST_AFTER_NO_RESPONSE_FOR_HOURS = 72

export const POST_FEEDBACK_RATINGS = ['Super Helpful', 'Helpful', 'Not Helpful'] as const

export const WORLD_SLUGS_LEAVING_DISALLOWED = [SCHOOLHOUSE_HUB_WORLD_SLUG]

export const SAT_BOOTCAMP_MATH_HELP_CHANNEL = 'SAT Math Help'
export const SAT_BOOTCAMP_RW_HELP_CHANNEL = 'SAT R&W Help'

export const SAT_BOOTCAMP_WORLD_TUTORS_CHANNEL = 'Tutors'

export const EDITABLE_POST_TYPES: PostType[] = ['user-post']

export const PUBLISH_BOOTCAMP_TODO_TITLE = 'Publish your first Bootcamp'

/* Subworld Visibility: who can view + who can join
  public: publicly listed + anyone-can-join
  hidden: unlisted + invite-only
  unlisted: unlisted + anyone-can-join
*/
export const VISIBLE_WORLD_TYPES: WorldVisibility[] = ['public', 'private']
export const INVITE_ONLY_WORLD_TYPES: WorldVisibility[] = ['private', 'hidden']

export const ADULT_WORLD_SLUGS = [COLLEGE_LOUNGE_WORLD_SLUG]

export const SHOW_COLLEGE_INFO_WORLD_SLUGS = [
  COLLEGE_LOUNGE_WORLD_SLUG,
  COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
  COLLEGE_PREP_WORLD_SLUG,
]
export const PRIVATE_WORLD_SLUGS = [SCHOOLHOUSE_TUTORS_WORLD_SLUG, COLLEGE_LOUNGE_WORLD_SLUG]

export const TUTORS_CHANNEL_NAME = 'Tutors'
export const SENIOR_TUTORS_CHANNEL_NAME = 'Senior Tutors'
export const SAT_BOOTCAMP_LEARNERS_CHANNEL_NAME = 'SAT Bootcamp Learners'
export const SAT_BOOTCAMP_TUTORS_CHANNEL_NAME = 'SAT Bootcamp Tutors'
export const COLLEGE_ADMISSIONS_WORKSHOPS_LEARNERS_CHANNEL_NAME =
  'College Admissions Workshop Learners'
export const COLLEGE_ADMISSIONS_WORKSHOPS_TUTORS_CHANNEL_NAME = 'College Admissions Workshop Tutors'
export const DIALOGUES_PARTICIPANTS_CHANNEL_NAME = 'Dialogues'
export const DIALOGUES_CLUB_PRESIDENTS_CHANNEL_NAME = 'Dialogues Club Presidents'

export const MOVING_AWAY_FROM_SUBWORLDS_POST_URL =
  'https://schoolhouse.world/dashboard/community/c/192?p=97473'
